/**
 * Fetches `calendar_available` for a given set of interviewer IDs via the same
 * `/calendar_events/trigger_fetch` + poll flow KInterviewScheduler uses. We only
 * need the `users_info[*].calendar_available` slice to gate the Schedule-with-AI
 * submit button and show the per-chip warning icon.
 */
import { useMemo } from 'react';
import moment from 'moment-timezone';
import { useQuery } from '@tanstack/react-query';
import { fetchCalendarEventsWithPolling } from '@/api/calendarEvents/calendarEventsApi';

interface Params {
  userIds: number[];
  timezone: string;
  atsJobId?: number | null;
  enabled?: boolean;
}

export function useInterviewerCalendarStatus({
  userIds,
  timezone,
  atsJobId,
  enabled = true,
}: Params) {
  const sortedIds = useMemo(
    () =>
      Array.from(new Set(userIds.filter((id) => Number.isFinite(id)))).sort(
        (a, b) => a - b
      ),
    [userIds]
  );

  const { startDate, endDate } = useMemo(
    () => ({
      startDate: moment().startOf('week').toISOString(),
      endDate: moment().endOf('week').toISOString(),
    }),
    []
  );

  const normalizedAtsJobId =
    typeof atsJobId === 'number' && atsJobId > 0 ? atsJobId : undefined;

  const query = useQuery({
    queryKey: [
      'schAiInterviewerCalendarStatus',
      sortedIds,
      normalizedAtsJobId ?? null,
    ],
    queryFn: ({ signal }) =>
      fetchCalendarEventsWithPolling(
        {
          userIds: sortedIds,
          startDate,
          endDate,
          timezone,
          ...(normalizedAtsJobId !== undefined && {
            atsJobId: normalizedAtsJobId,
          }),
        },
        { signal }
      ),
    enabled: enabled && sortedIds.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const calendarAvailabilityById = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const u of query.data?.usersInfo ?? []) {
      if (u?.id !== null && u?.id !== undefined) {
        map[String(u.id)] = Boolean(u.calendar_available);
      }
    }
    return map;
  }, [query.data]);

  return {
    calendarAvailabilityById,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
  };
}
